import React, { useState, useContext, useEffect, useCallback } from 'react'
import { get, post, put, upload } from '../modules/fetch'
import Image from './Image'
import Input from './Input'
import Icon from './Icon'
import FileInput from './FileInput'
import { If, debounce, urlText } from '../modules/helpers'
import { ArticleEditContext, TranslationContext } from './ArticleEdit'
import { InlineLoading } from './Loading'
import Flag from './Flag'
import URI from 'urijs'
import { ENABLED_API_LOCALE_ARRAY, FRONT_STORE_ARRAY } from '../constants/'
import useId from '../hooks/useId'
import Button from './Button'
const uuid = require('uuid')

// no image object? it's fine, we'll put instead of post

const getEditableFileName = (filename) => {
    if (!!~filename?.lastIndexOf('-')) {
        return filename?.substring(0, filename?.lastIndexOf('-'))
    } else {
        return filename?.substring(0, filename?.lastIndexOf('.'))
    }
}
const getStaticPartInFilename = (filename) => {
    if (!!~filename?.lastIndexOf('-')) {
        return filename?.substring(filename?.lastIndexOf('-'))
    } else {
        return filename?.substring(filename?.lastIndexOf('.'))
    }
}

const removeParam = (param) => (url) => {
    const target = url && URI(url)
    return target && target.removeSearch(param).toString()
}
const removeTag = removeParam('tag')
const dereferrer = (url) => 'https://dereferer.me/?' + escape(url)

const ImageUpload = React.memo(function ImageUpload(props) {
    const id = useId()
    const { link, display_external_id, article, content, original_content = article?.translated_from__article_id ? content : undefined } = props
    // console.log({ props }, { original_content })

    const [image, setImage] = useState(() => ({
        ...(props.image || { key: props.image_key || undefined }),
        path: (article?.site?.key || props.image?.path) + '/',
    }))
    const [base64, setBase64] = useState('')
    const [hash, setHash] = useState()
    const [changeImage, setChangeImage] = useState(false)
    const [isProviderLoading, setIsProviderLoading] = useState(false)
    const [isProviderError, setIsProviderError] = useState(false)
    const [rescued, setRescued] = useState(false)

    const { setOnSaveArray, translateText, unavailableLinkData, clickData, setAmazonSelectorVisible, setProductSwapInfo } = useContext(ArticleEditContext)

    const unavailable = unavailableLinkData?.data?.find((i) => i.external_id === link?.external_id)

    const block_id = props.content?.block_id || props.block_id

    const click_total = (clickData && clickData.data && clickData.data.length) || 0
    const click_array = (clickData && clickData.data && clickData.data.filter((c) => c.block_id === block_id)) || []
    // console.log({ click_array })

    const { translationContext, setTranslationContext } = useContext(TranslationContext)

    const displayExternalIdButton = display_external_id && ENABLED_API_LOCALE_ARRAY.includes(article.site?.locale)
    const caption_content =
        (image.alt && image.href && (
            <a alt="" href={image.href} target="_blank" rel="noopener noreferrer">
                {image.alt}
            </a>
        )) ||
        image.alt ||
        null
    const caption = (caption_content && <figcaption>{caption_content}</figcaption>) || null

    const caption_error = !image.alt
    const provider_url = FRONT_STORE_ARRAY.find((f) => f.locale === article?.site?.locale)?.url
    const provider_error = image.href && !image.href?.includes(provider_url) && !image.href.includes(article?.site?.url) && image.href.substring(0, 1) !== '/'
    const tag_error =
        (image.href &&
            article?.site?.tracking_id &&
            !~image.href.indexOf(article.site.tracking_id) &&
            !~image.href.indexOf('//' + article.site.url + '/') &&
            image.href.substring(0, 1) !== '/') ||
        image.href?.includes('{{link_id}}')
    const href_error = provider_error || tag_error
    const filename_error = getEditableFileName(image.temp_src || image.src)?.length === 11 && !~getEditableFileName(image.temp_src || image.src).indexOf('-')
    const external_id_error = display_external_id && !link?.external_id
    const image_error = caption_error || href_error || filename_error || external_id_error

    const getHrefFromExternalId = useCallback(() => {
        setIsProviderLoading(true)
        setIsProviderError(false)
        setIsProviderError(false)
        return get(
            'provider/amazon/product_details?ASIN=' +
                link?.external_id +
                '&tracking_id=' +
                props.article?.site?.tracking_id +
                '&locale=' +
                props.article?.site?.locale
        )
            .then((data) => {
                console.log({ data })
                if (data?.href) {
                    setImageContent({ href: data?.[0]?.href })
                }
                setIsProviderLoading(false)
            })
            .catch((err) => {
                setIsProviderLoading(false)
                setIsProviderError(true)
            })
    }, [link?.external_id, props.article?.site?.tracking_id, props.article?.site?.locale])

    const getHrefFromExternalIdForBatch = () => {
        return new Promise((resolve, reject) => {
            getHrefFromExternalId().then(() => {
                setTranslationContext((c) => ({
                    ...c,
                    getHrefFromExternalIdArray: (c.getHrefFromExternalIdArray || []).filter((i) => i.id !== id),
                }))
                setTimeout(() => {
                    console.log(
                        'resolving',
                        'provider/amazon/product_details?ASIN=' +
                            link?.external_id +
                            '&tracking_id=' +
                            props.article?.site?.tracking_id +
                            '&locale=' +
                            props.article?.site?.locale
                    )
                    resolve()
                }, 1000)
            })
        })
    }

    const translateCaptionForBatch = useCallback(() => {
        return new Promise((resolve, reject) => {
            translateText({
                text: image.alt,
                source_locale: article?.translated_from__locale,
                target_locale: article?.locale,
            }).then(({ text }) => {
                setImageContent({ alt: text, src: urlText(text) + getStaticPartInFilename(image.temp_src || image.src) })
                setTimeout(() => resolve(), 500)
                return { text }
            })
        })
    }, [image.alt, image.src, image.temp_src, image.path, article?.translated_from__locale, article?.locale])

    useEffect(() => {
        setTranslationContext &&
            displayExternalIdButton &&
            href_error &&
            !external_id_error &&
            !translationContext?.getHrefFromExternalIdArray.find((i) => i.id === id) &&
            setTranslationContext((c) => ({
                ...c,
                getHrefFromExternalIdArray: [...(c.getHrefFromExternalIdArray || []), { id, Fn: getHrefFromExternalIdForBatch }],
            }))

        setTranslationContext &&
            article?.translated_from__article_id &&
            !translationContext?.translateImageCaptionArray.find((i) => i.id === id) &&
            setTranslationContext((c) => ({
                ...c,
                translateImageCaptionArray: [...(c.translateImageCaptionArray || []), { id, Fn: translateCaptionForBatch }],
            }))
        return () => {
            setTranslationContext &&
                setTranslationContext((c) => ({
                    ...c,
                    getHrefFromExternalIdArray: (c.getHrefFromExternalIdArray || []).filter((i) => i.id !== id),
                    translateImageCaptionArray: (c.translateImageCaptionArray || []).filter((i) => i.id !== id),
                }))
        }
    }, [getHrefFromExternalId, translateCaptionForBatch, translationContext, image.href])

    const setImageContent = (img) => {
        // console.log({image})
        let payload, _img
        setImage((i) => {
            /* _img = {
                id: img.id || i.id,
                path: img.path ?? i.path,
                key: props.image_key || undefined,
                src: img.src ?? i.src,
                alt: img.alt ?? i.alt,
                href: img.href ?? i.href,
                width: img.width ?? i.width,
                height: img.height ?? i.height,
            } */
            _img = { key: props.image_key || undefined, ...i, ...img, path: (article?.site?.key || img.path || i.path) + '/' }
            payload = { ..._img, temp_src: _img.src, src: image.src, modified: true }
            return payload
        })
    }
    const setImageValidated = () => {
        setImage((i) => {
            return { ...i, validated_for__article_id: article.id }
        })
        props.onChange({ target: { value: { ...image, validated_for__article_id: article.id } } })
    }

    const onChangeCaption = (e) => setImageContent({ alt: e.target.value })

    const onChangeSrc = (e) => {
        const static_end = getStaticPartInFilename(image.temp_src || image.src)
        setImageContent({ src: urlText(e.target.value).substring(0, 255 - static_end.length) + static_end })
    }

    useEffect(() => {
        if (image.modified) {
            const { modified, site, ...payload } = image
            props.onChange({ target: { value: { ...payload, src: payload.temp_src || payload.src } } })
            syncWithDB({ ...payload, src: payload.temp_src || payload.src })
        }
    }, [image])

    useEffect(() => {
        // run series of checks on image integrity
        if (image && !image.id) {
            // try to deduce the id from src
            if (image.src) {
                const id = image.src.substring(image.src.lastIndexOf('-') + 1, image.src.lastIndexOf('.'))
                if (String(id) == Number(id)) {
                    console.error('Image without ID', image)
                    // id found
                    console.log('ID found:', id)
                    !rescued &&
                        get('image/' + id).then((i) => {
                            setImageContent(i)
                            setRescued(true)
                        })
                }
            }
        }
    }, [image])

    const syncWithDB = useCallback(
        debounce((img) => {
            console.log(img)
            if (img.id) {
                put('image/' + img.id, img).then((r) => {
                    const filtered_image = { id: r.id, path: r.path, alt: r.alt, href: r.href || img.href, width: r.width, height: r.height }
                    let payload
                    setImage((current) => {
                        // console.log({ current })
                        const { modified, ..._current } = current
                        // console.log({ _current })
                        const src = _current.temp_src.includes('http://') || _current.temp_src.includes('https://') ? r.src : _current.temp_src
                        const temp_src = _current.temp_src.includes('http://') || _current.temp_src.includes('https://') ? r.src : _current.temp_src
                        payload = { ..._current, ...filtered_image, alt: _current.alt, src, temp_src }
                        return payload
                    })
                    props.onChange({ target: { value: payload } })
                })
            } else {
                console.error(`No image id, can't sync with DB`)
            }
        }, 1000),
        []
    )

    const onUploadChange = (e) => {
        let files = e.target.files || e.dataTransfer.files
        console.log(e.target.files)
        if (!files.length) {
            return
        }
        createImage(files[0])
    }

    const ensureImageObjectInDb = (img) => {
        return new Promise((resolve) => {
            if (props.image && props.image.id) {
                resolve()
            } else {
                post('image', img).then((r) => {
                    const filtered_image = { id: r.id, path: r.path, src: r.src, alt: r.alt, href: r.href || img.href, width: r.width, height: r.height }
                    let payload
                    setImage((current) => {
                        payload = { ...current, ...filtered_image }
                        return payload
                    })
                    props.onChange({ target: { value: payload } })
                    resolve()
                })
            }
        })
    }

    const createImage = (file) => {
        console.log('createImage')
        const parent_model = props.parent_model || image?.parent_model
        const parent_id = props.parent_id || image?.parent_id
        // create image object in db
        ensureImageObjectInDb({ parent_model, parent_id, site_id: props.site_id }).then(() => {
            let reader = new FileReader()
            reader.onload = (e) => {
                setBase64(e.target.result)
                setChangeImage(false)
                fileUpload(file.name, e.target.result)
            }
            reader.readAsDataURL(file)
        })
    }

    const fileUpload = async (image_name, base64) => {
        const parent_model = props.parent_model || image?.parent_model
        const parent_id = props.parent_id || image?.parent_id
        upload('image-upload', {
            id: image.id,
            image_name,
            file: base64.substr(base64.indexOf('base64,') + 7),
            parent_model,
            parent_id,
        })
            .then((r) => r.json())
            .then((r) => {
                console.log(r)
                setImageContent(r)
                setHash(uuid.v4())
            })
            .catch((e) => {
                console.error(e)
            })
    }

    return (
        <div
            className={
                'image-edit-container w-100 text-center' +
                (unavailable ? ' image-warning' : '') +
                (image_error ? ' image-error' : '') +
                (changeImage ? ' image-edit-upload' : '')
            }
        >
            {(!!image.src && (
                <>
                    <div className="position-relative hover-appear">
                        <small className="position-absolute text-right alt-visible" style={{ top: '-22px', right: 0 }}>
                            <a href="#" onClick={(e) => e.preventDefault() || setChangeImage(!changeImage)}>
                                {changeImage ? 'Cancel' : 'Change image'}
                            </a>
                        </small>
                        {/* {console.log('here', image.temp_src, image.src, !!image.src, image.temp_src||image.src)} */}
                        <Image {...image} src={image.src + (hash ? '?' + hash : '')} key={'image'} />
                        <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                            {(unavailable && (
                                <>
                                    <span className="badge badge-warning d-block mb-2">Unavailable</span>
                                </>
                            )) ||
                                null}
                            {(click_array && block_id && (
                                <>
                                    <span className="badge badge-secondary d-block mb-2">
                                        {click_array.length} click{click_array.length !== 1 ? 's' : ''}{' '}
                                        {(click_total && <>{((click_array.length / click_total) * 100).toFixed(2)}%</>) || null}
                                    </span>
                                </>
                            )) ||
                                null}
                        </div>
                        <CheckImage {...props} setImageContent={setImageContent} setImageValidated={setImageValidated} />
                        {(!changeImage && (
                            <>
                                <div
                                    className="text-left w-100 target p-3 pb-0"
                                    style={{ position: 'absolute', top: '0', left: '0', backgroundColor: 'rgba(255, 255, 255, .9)' }}
                                >
                                    {(props.link && article?.translated_from__locale && (
                                        <Button
                                            className="btn-primary btn-xs position-absolute"
                                            style={{ top: '1rem', right: '1rem' }}
                                            onClick={() => {
                                                console.log(props)

                                                // setProductSwapInfo({
                                                //     keywords: `Eleasica Girls Cosplay Dress Princess Elsa Long Sleeve Snow Queen Long Dress Costume Soft Blue Dress Party Halloween Christmas,110,Blue,4-5 years`,
                                                //     original_content: content,
                                                //     update: (payload) => {
                                                //         const { external_id, href, src } = payload
                                                //         console.log({ payload })
                                                //         // setOnSaveArray((s) => [
                                                //         //     ...s,
                                                //         //     { method: put, url: 'link/' + link.id, params: { external_id, href, src } },
                                                //         // ])

                                                //         put('link/' + link.id, { external_id, href, src })
                                                //         setImageContent({ external_id, href })
                                                //         props.onLinkChange({ ...link, external_id, href, src })
                                                //     },
                                                // })
                                                // setAmazonSelectorVisible(true)

                                                translateText({
                                                    text: content?.label,
                                                    source_locale: article?.translated_from__locale,
                                                    target_locale: article?.locale,
                                                }).then(({ text }) => {
                                                    console.log('Translated product label:', { text })
                                                    setProductSwapInfo({
                                                        keywords: text,
                                                        original_content: content,
                                                        update: (payload) => {
                                                            const { external_id, href, src } = payload
                                                            // setOnSaveArray((s) => [
                                                            //     ...s,
                                                            //     { method: put, url: 'link/' + link.id, params: { external_id, href } },
                                                            // ])

                                                            put('link/' + link.id, { external_id, href })
                                                            setImageContent({ href, src })
                                                            props.onLinkChange({ ...link, external_id, href })
                                                        },
                                                    })
                                                    setAmazonSelectorVisible(true)
                                                })
                                            }}
                                        >
                                            Product Swap
                                        </Button>
                                    )) ||
                                        null}
                                    <label htmlFor={`image-alt-${image.id}`}>Caption</label>
                                    <Input
                                        id={`image-alt-${image.id}`}
                                        className={'' + (caption_error ? ' image-error' : '')}
                                        type="text"
                                        value={image.alt}
                                        onChange={onChangeCaption}
                                        article={props.parent}
                                    />
                                    {(props.display_target && (
                                        <>
                                            <label htmlFor={`image-href-${image.id}`} className="mt-2">
                                                Target
                                            </label>
                                            <Input
                                                id={`image-href-${image.id}`}
                                                className={'' + (href_error ? ' image-error' : '')}
                                                type="text"
                                                value={image.href}
                                                onChange={(e) => {
                                                    setImageContent({ href: e.target.value })
                                                }}
                                                data-image-id={image.id}
                                                append={
                                                    <div className="input-group-append">
                                                        <a
                                                            className="input-group-text pointer"
                                                            href={dereferrer(removeTag(image.href))}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <Icon name="external" />
                                                        </a>
                                                    </div>
                                                }
                                            />
                                        </>
                                    )) ||
                                        null}
                                    {(display_external_id && (
                                        <>
                                            <label htmlFor={`image-external-id-${image.id}`} className="mt-2">
                                                ASIN
                                            </label>
                                            <Input
                                                className={'' + (external_id_error ? ' image-error' : '')}
                                                type="text"
                                                id={`image-external-id-${image.id}`}
                                                value={link?.external_id}
                                                onChange={(e) => {
                                                    const external_id = e.target.value
                                                    props.onLinkChange({ ...link, external_id })
                                                    // ['link/' + link.id, { external_id: e.target.value }]
                                                    setOnSaveArray((s) => [...s, { method: put, url: 'link/' + link.id, params: { external_id } }])
                                                    !link?.id && console.error('No link')
                                                }}
                                                data-image-id={image.id}
                                                append={
                                                    (displayExternalIdButton && (
                                                        <div className="input-group-append">
                                                            <span className="input-group-text pointer" onClick={getHrefFromExternalId}>
                                                                {(isProviderLoading && <InlineLoading key="loading" className={'spinner-sm'} />) || (
                                                                    <Icon name="amazon" key="amazon" />
                                                                )}
                                                                <Flag locale={article.site?.locale} className="ml-1" />
                                                            </span>
                                                        </div>
                                                    )) || (
                                                        <div className="input-group-append">
                                                            <a
                                                                className={
                                                                    'input-group-text pointer external-id-manual-search' +
                                                                    (external_id_error ? ' external-id-error' : '') +
                                                                    (href_error ? ' href-error' : '')
                                                                }
                                                                href={FRONT_STORE_ARRAY.find(
                                                                    (i) => i.locale === article.site?.locale
                                                                )?.external_id_manual_search(link?.external_id, article.site?.tracking_id)}
                                                                data-image-id={image.id}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {(isProviderLoading && <InlineLoading key="loading" className={'spinner-sm'} />) || (
                                                                    <Icon name="amazon" key="amazon" />
                                                                )}
                                                                <Flag locale={article.site?.locale} className="ml-1" />
                                                                <Icon name="external" className="ml-1" />
                                                            </a>
                                                        </div>
                                                    )
                                                }
                                            />
                                        </>
                                    )) ||
                                        null}
                                    <label htmlFor={`image-src-${image.id}`} className="mt-2">
                                        Filename
                                    </label>
                                    <div className="input-group">
                                        <Input
                                            id={`image-src-${image.id}`}
                                            className={'' + (filename_error ? ' image-error' : '')}
                                            type="text"
                                            value={getEditableFileName(image.temp_src || image.src)}
                                            onChange={onChangeSrc}
                                            article={props.parent}
                                            append={
                                                <div className="input-group-append">
                                                    <span className="input-group-text">{getStaticPartInFilename(image.temp_src || image.src)}</span>
                                                </div>
                                            }
                                        />
                                    </div>
                                </div>
                            </>
                        )) || (
                            <>
                                <div className="position-absolute flex flex-column" style={{ top: '50%', left: '50%', marginLeft: '-78px' }}>
                                    <div>
                                        <FileInput type="file" onChange={onUploadChange} />
                                    </div>
                                    <div>
                                        or{' '}
                                        <a href="#" onClick={(e) => e.preventDefault() || setChangeImage(!changeImage)}>
                                            Cancel
                                        </a>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    {caption}
                </>
            )) ||
                null}
            <If condition={!image.src}>
                <If condition={!base64}>
                    <div className="">
                        <FileInput type="file" onChange={onUploadChange} />
                    </div>
                </If>
                <If condition={base64}>
                    <img alt="" className="img-fluid" src={base64} onDragStart={(e) => e.preventDefault()} />
                </If>
            </If>
        </div>
    )
})

const CheckImage = (props) => {
    const { image, link, article, setImageContent, setImageValidated } = props

    useEffect(() => {
        if (
            article &&
            article.translated_from__article_id &&
            image &&
            image.id &&
            link &&
            link.id &&
            (!image.validated_for__article_id || image.validated_for__article_id === article.id)
        ) {
            console.log('ready to check', image.id, link.id)
            get('image/' + image.id).then((image) => {
                const not_the_same_locale = image.locale && article.site.locale !== image.locale
                const not_the_same_link_id = image.link_id && image.link_id !== link.id
                if (not_the_same_locale || not_the_same_link_id) {
                    console.log({ image }, 'needs to be changed', { not_the_same_locale }, { not_the_same_link_id })
                    post('image/fix-wrong-id', { image_id: image.id, link_id: link.id })
                        .then((i) => {
                            setImageContent(i)
                        })
                        .catch(console.error)
                } else if ((image.locale && article.site.locale === image.locale) || (image.link_id && image.link_id === link.id)) {
                    // image is verified
                    console.log('Image validated')
                    setImageValidated()
                }
            })
        }
    }, [image && image.id, link && link.id])

    return null
}

export default ImageUpload
